import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Download as DownloadIcon, Loader2, X as CloseIcon } from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/ui/button/index.js";
import { cn } from "../../../lib/utils.js";
import { downloadActionI18nKey, type AttachmentDownloadStatus } from "../attachment-download-state.js";
import {
  attachmentDownloadFilename,
  downloadAttachmentFromSource,
  type AttachmentMediaSource,
} from "../attachment-media-actions.js";
import { shouldCloseImagePreviewOnBackdropPointerDown } from "../message-image-preview-logic.js";
import { useAttachmentDownload } from "../use-attachment-download.js";

export function MediaLightboxShell({
  open,
  onOpenChange,
  title,
  description,
  onDownload,
  downloadStatus,
  downloadDisabled,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  onDownload: () => void;
  downloadStatus: AttachmentDownloadStatus;
  downloadDisabled: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();

  const handleOverlayPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (shouldCloseImagePreviewOnBackdropPointerDown(event.target, event.currentTarget)) {
        onOpenChange(false);
      }
    },
    [onOpenChange],
  );

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-[var(--z-alert-overlay)] bg-ink/85 animate-overlay-in data-[state=open]:animate-overlay-in"
          onPointerDown={handleOverlayPointerDown}
        />
        <DialogPrimitive.Content
          className={cn(
            "fixed inset-0 z-[var(--z-alert)] flex flex-col outline-none pointer-events-none",
            "data-[state=open]:animate-overlay-in",
          )}
        >
          <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">{description}</DialogPrimitive.Description>

          <div className="pointer-events-auto flex items-center justify-end gap-2 px-3 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="border border-line/40 bg-ink/35 text-surface-elevated hover:bg-ink/50"
              disabled={downloadDisabled}
              onClick={onDownload}
            >
              {downloadStatus === "downloading" ? (
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              ) : (
                <DownloadIcon className="size-4" aria-hidden="true" />
              )}
              {t(downloadActionI18nKey(downloadStatus))}
            </Button>
            <DialogPrimitive.Close asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="border border-line/40 bg-ink/35 text-surface-elevated hover:bg-ink/50"
                aria-label={t("inbox.media.preview.close")}
              >
                <CloseIcon className="size-4" aria-hidden="true" />
              </Button>
            </DialogPrimitive.Close>
          </div>

          <div className="pointer-events-none flex min-h-0 flex-1 items-center justify-center px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            {children}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function MessageVideoPreview({
  open,
  onOpenChange,
  videoUrl,
  fileName,
  mimeType,
  organizationId,
  attachmentId,
  ensureUrl,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  videoUrl: string;
  fileName?: string | null;
  mimeType?: string | null;
  organizationId?: number;
  attachmentId?: string;
  ensureUrl?: () => Promise<string | null>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const download = useAttachmentDownload();
  const [playbackUrl, setPlaybackUrl] = useState(videoUrl);

  const resolveMediaSource = useCallback(async (): Promise<AttachmentMediaSource | null> => {
    if (organizationId != null && attachmentId) {
      return { kind: "stored", organizationId, attachmentId };
    }
    const url = ensureUrl ? await ensureUrl() : playbackUrl;
    if (!url) {
      return null;
    }
    return { kind: "url", url };
  }, [attachmentId, ensureUrl, organizationId, playbackUrl]);

  const handleDownload = useCallback(async () => {
    await download.run(async () => {
      const source = await resolveMediaSource();
      if (!source) {
        throw new Error("download_failed");
      }
      await downloadAttachmentFromSource(
        source,
        attachmentDownloadFilename(fileName, mimeType, "video"),
      );
    });
  }, [download, fileName, mimeType, resolveMediaSource]);

  const handleOpenChange = useCallback(
    async (nextOpen: boolean) => {
      if (nextOpen && ensureUrl) {
        const fresh = await ensureUrl();
        if (fresh) {
          setPlaybackUrl(fresh);
        }
      }
      onOpenChange(nextOpen);
    },
    [ensureUrl, onOpenChange],
  );

  return (
    <MediaLightboxShell
      open={open}
      onOpenChange={(next) => void handleOpenChange(next)}
      title={t("inbox.media.preview.videoTitle", {
        label: fileName ?? t("inbox.messageType.VIDEO"),
      })}
      description={t("inbox.media.preview.videoDescription")}
      onDownload={() => void handleDownload()}
      downloadStatus={download.status}
      downloadDisabled={download.disabled}
    >
      <video
        src={playbackUrl}
        controls
        playsInline
        preload="metadata"
        className="pointer-events-auto max-h-[min(82dvh,100%)] max-w-[min(96vw,100%)] rounded-md bg-ink"
      />
    </MediaLightboxShell>
  );
}
